import { Card, HStack, Image, Text, Tooltip, VStack } from '@chakra-ui/react';
import React from 'react';
import CustomButton from '@/components/atoms/CustomButton';
import iconsPng from '@/assets/img/png/icons';
import SvgIcon from '@/components/atoms/SvgIcon';
import svgIcons from '@/assets/svg/icons';
import { useNavigate } from '@tanstack/react-router';
import sharedRoutes from '@/constants/routes/shared';
import illustrations from '@/assets/img/png/ilustrations';
import { extra, text } from '@/theme/colors/palettes';
import { TSupportTeamCount } from '@/types/User.types';

const CARD_HEIGHT = '400px';

const SUPPORT_TEAM_CARDS: {
  teamName: string;
  imageSrc: string;
  imageWidth: string;
  imageHeight: string;
  subtitle: string;
  description: string;
  buttonText: string;
  navigateTo: string;
  type: keyof TSupportTeamCount | 'coach';
}[] = [
  {
    teamName: 'Peer Support',
    imageSrc: illustrations.headToHead,
    imageWidth: '80px',
    imageHeight: '80px',
    subtitle: 'Members from your team',
    description:
      'You have members in your team that are certified Peer Support Members and they can help you with various issues while being your very close colleagues.',
    buttonText: 'View Peer Support Team',
    navigateTo: '/directory/peer-support',
    type: 'peerSupports',
  },
  {
    teamName: 'Therapists',
    imageSrc: illustrations.nurse,
    imageWidth: '80px',
    imageHeight: '80px',
    subtitle: 'Vetted therapists on platform',
    description:
      'All therapists are vetted and trained to provide support on a variety of topics. Check our therapists directory and find one that best suits your needs.',
    buttonText: 'View MyOmnia Therapists',
    navigateTo: '/directory/provider',
    type: 'therapists',
  },
  {
    teamName: 'Coaches',
    imageSrc: illustrations.coach,
    imageWidth: '80px',
    imageHeight: '80px',
    subtitle: 'Coaches available to help',
    description:
      'MyOmnia coaches can guide you through different occasions, teach you different skills or approaches - from career advice to personal growth.',
    buttonText: 'View MyOmnia Coaches',
    navigateTo: '/directory/coach',
    type: 'coach',
  },
];

interface Props {
  supportTeamCounts: TSupportTeamCount;
}

const UserSupportTeamSection = ({ supportTeamCounts }: Props) => {
  const navigate = useNavigate();
  const noSupportTeamMembers = Object.values(supportTeamCounts).every((count) => count === 0);

  if (noSupportTeamMembers)
    return (
      <VStack width={'100%'} height={CARD_HEIGHT} justify={'center'} alignItems={'center'} paddingBottom={'30px'}>
        <Card
          size="sm"
          minWidth={'370px'}
          minHeight={'265px'}
          variant={'elevated'}
          borderRadius={'20px'}
          padding={'30px'}
          shadow={'0px 0px 20px 0px rgba(0, 65, 121, 0.15)'}
          alignItems={'center'}
          justifyContent={'center'}
        >
          <VStack position={'absolute'} top={-50}>
            <Image alt="notepad" src={iconsPng.notepad} width={100} height={100} />
          </VStack>
          <Text
            variant={'lora'}
            fontSize={'22px'}
            lineHeight={'22px'}
            fontWeight={500}
            color={'text.mediumBlue'}
            fontStyle={'italic'}
            marginTop={'50px'}
          >
            No support team members assigned?
          </Text>

          <CustomButton
            hoverOpacityDrop
            label="Contact us"
            style={{ marginTop: '40px' }}
            backgroundColor="primary.500"
            labelColor="extra.white"
            iconBeforeLabelJSX={<SvgIcon iconPath={svgIcons.email} size={19} color={'white'} />}
            onClick={() => {
              navigate({ to: sharedRoutes.contactSupport });
            }}
          />
        </Card>
      </VStack>
    );

  return (
    <HStack width={'100%'} gap={'20px'} marginTop={'40px'}>
      {SUPPORT_TEAM_CARDS.map((supportTeamCard) => {
        return (
          <VStack
            key={supportTeamCard.type}
            padding={'20px'}
            width={'400px'}
            height={'265px'}
            boxShadow="0px 8px 40px 0px #0000001A"
            backgroundColor={extra.white}
            alignItems={'start'}
            justify={'space-between'}
            gap={'22px'}
            borderRadius={'10px'}
          >
            <HStack gap={'26px'} justifyContent={'start'} padding={'1px'}>
              <Image
                // paddingLeft={'10px'}
                alt={supportTeamCard.teamName}
                height={supportTeamCard.imageHeight}
                width={supportTeamCard.imageWidth}
                src={supportTeamCard.imageSrc}
              />
              <VStack gap={'6px'} alignItems={'start'}>
                <Text variant={'loraMedium22'} color={text.darkBlue}>
                  {supportTeamCard.teamName}
                </Text>
                <Text variant={'urbanistBold'} color={text.mediumGray}>
                  {supportTeamCard.type === 'coach'
                    ? supportTeamCard.subtitle
                    : `${supportTeamCounts[supportTeamCard.type]} ${supportTeamCard.subtitle}`}
                </Text>
              </VStack>
            </HStack>

            <Text variant={'urbanistSemiBoldRegular'} color={text.mediumBlue}>
              {supportTeamCard.description}
            </Text>

            <Tooltip
              label="Coaches Coming Soon..."
              visibility={supportTeamCard.type === 'coach' ? 'visible' : 'hidden'}
              backgroundColor={'white'}
              rounded={'6px'}
              padding={'10px'}
              color={'text.mediumBlue'}
            >
              <HStack width={'100%'}>
                <CustomButton
                  noShadow
                  hoverOpacityDrop={supportTeamCard.type !== 'coach'}
                  label={supportTeamCard.buttonText}
                  disabled={supportTeamCard.type === 'coach'}
                  disabledColor="white"
                  iconAfterLabelSvgPath={svgIcons.rightArrow}
                  iconAfterLabelSvgSize={11}
                  iconAfterLabelSvgBoxHeight={14}
                  iconAfterLabelSvgBoxWidth={14}
                  height={'54px'}
                  width={'100%'}
                  style={{
                    padding: 0,
                    borderColor: text.mediumBlue,
                    borderWidth: '2px',
                    opacity: supportTeamCard.type === 'coach' ? 0.5 : undefined,
                  }}
                  labelColor={text.mediumBlue}
                  onClick={() => navigate({ to: supportTeamCard.navigateTo })}
                />
              </HStack>
            </Tooltip>
          </VStack>
        );
      })}
    </HStack>
  );
};

export default UserSupportTeamSection;
